/**
 * ProjectEditor - Tabbed editor for cold storage projects with multi-scenario support
 *
 * When no scenario is selected, shows the ScenarioList.
 * When a scenario is selected, shows zones/results for that scenario.
 */

import React, { useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Box, Typography, Alert, Button, CircularProgress, IconButton, Breadcrumbs } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { vortxColors, vortxTypography, useThemeMode, useRetroContextSafe, SaveStatusIndicator } from '@vortx/shared-ui';
import { useColdProject } from '../hooks/useColdProject';
import { useColdScenarios } from '../hooks/useColdScenarios';
import { useColdOptimization } from '../hooks/useColdOptimization';
import ScenarioList from '../components/ScenarioList';
import ZonesSection from '../components/sections/ZonesSection';
import ResultsSection from '../components/sections/ResultsSection';

export default function ProjectEditor() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';
    const { coldSection } = useRetroContextSafe();

    const {
        project,
        loading,
        saveStatus: projectSaveStatus,
        lastSavedAt: projectLastSavedAt,
        error,
        updateLoadRelationship,
    } = useColdProject(projectId);

    const {
        scenarios,
        selectedScenario,
        loading: scenariosLoading,
        saveStatus: scenarioSaveStatus,
        lastSavedAt: scenarioLastSavedAt,
        createScenario,
        deleteScenario,
        duplicateScenario,
        selectScenario,
        deselectScenario,
        updateScenario,
        saveScenario,
    } = useColdScenarios(projectId, project);

    // Lift optimization state so it persists across tab switches
    const optimization = useColdOptimization();

    // Use context section (from RetroNavigation) or default to 'zones'
    const activeSection = coldSection || 'zones';

    // When scenario zones change, update via scenario hook
    const handleUpdateZones = useCallback((zones) => {
        updateScenario({ zones });
    }, [updateScenario]);

    // Save optimization results to the selected scenario.
    // Full results are sent to the backend which uploads them to GCS
    // and stores a compact summary in Firestore.
    const handleSaveResults = useCallback(async (results, period) => {
        if (!selectedScenario) return;

        updateScenario({ results, results_period: period });
        await saveScenario();
    }, [selectedScenario, updateScenario, saveScenario]);

    // Determine which save status to show (scenario when editing, project otherwise)
    const activeSaveStatus = selectedScenario ? scenarioSaveStatus : projectSaveStatus;
    const activeLastSavedAt = selectedScenario ? scenarioLastSavedAt : projectLastSavedAt;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: '#2196F3' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!project) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning">Project not found</Alert>
            </Box>
        );
    }

    const renderSection = () => {
        // If no scenario selected, always show the scenario list
        if (!selectedScenario) {
            return (
                <ScenarioList
                    scenarios={scenarios}
                    isLoading={scenariosLoading}
                    onSelect={selectScenario}
                    onCreate={() => createScenario()}
                    onDuplicate={duplicateScenario}
                    onDelete={deleteScenario}
                    selectedId={null}
                />
            );
        }

        // Scenario is selected — show zones or results based on active section
        switch (activeSection) {
            case 'zones':
                return (
                    <ZonesSection
                        zones={selectedScenario.zones || []}
                        updateZones={handleUpdateZones}
                        project={project}
                        onUpdateLoadRelationship={updateLoadRelationship}
                    />
                );
            case 'results':
                return (
                    <ResultsSection
                        projectData={{ zones: selectedScenario.zones || [] }}
                        project={project}
                        optimization={optimization}
                        savedResults={selectedScenario.results || null}
                        onSaveResults={handleSaveResults}
                        resultsStale={false}
                    />
                );
            default:
                return <Alert severity="warning">Unknown section: {activeSection}</Alert>;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Project Header */}
            <Box
                sx={{
                    p: 3,
                    borderBottom: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
                    bgcolor: isRetro ? 'rgba(0, 0, 0, 0.2)' : 'background.paper',
                    flexShrink: 0,
                }}
            >
                {/* Breadcrumb - hide in retro mode */}
                {!isRetro && (
                    <Breadcrumbs sx={{ mb: 2 }}>
                        <Link to="/cold-storage" style={{ color: '#2196F3', textDecoration: 'none' }}>
                            Projects
                        </Link>
                        <Typography color="text.secondary">
                            {project.name}
                        </Typography>
                        {selectedScenario && (
                            <Typography color="text.secondary">
                                {selectedScenario.name}
                            </Typography>
                        )}
                    </Breadcrumbs>
                )}

                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => {
                                if (selectedScenario) {
                                    deselectScenario();
                                } else {
                                    navigate('/cold-storage');
                                }
                            }}
                            sx={isRetro ? { color: '#2196F3' } : {}}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography
                                variant={isRetro ? undefined : "h4"}
                                sx={isRetro ? {
                                    fontFamily: vortxTypography.heading,
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: '#2196F3',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    textShadow: `0 0 15px rgba(33, 150, 243, 0.3)`,
                                } : {
                                    fontFamily: vortxTypography.heading,
                                    color: 'text.primary',
                                    fontWeight: 700
                                }}
                            >
                                {selectedScenario ? selectedScenario.name : project.name}
                            </Typography>
                            {!selectedScenario && project.description && !isRetro && (
                                <Typography variant="body2" sx={{ color: vortxColors.stone, mt: 0.5 }}>
                                    {project.description}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {selectedScenario && scenarioSaveStatus === 'dirty' && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={saveScenario}
                                sx={{
                                    bgcolor: vortxColors.gold,
                                    '&:hover': { bgcolor: vortxColors.gold, filter: 'brightness(0.9)' },
                                }}
                            >
                                Save
                            </Button>
                        )}
                        <SaveStatusIndicator
                            status={activeSaveStatus}
                            lastSavedAt={activeLastSavedAt}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Active Section Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
                {renderSection()}
            </Box>
        </Box>
    );
}
